<template>
  <div>
    <v-layout class="relative-position">
      <v-flex :class="['search-bar', searchError && 'search-bar--error']">
        <v-text-field
            :label="globalSearchConstants.SEARCH_PLACEHOLDER"
            prepend-icon="search"
            outline
            clearable
            @keyup="handleKeyup"
            single-line
            data-qe-id="ypoSearch_input_box"
            v-model="ypoSearchText"
            @click:clear="toggleTray()"
            @focus="bringInTheTray()"
        >
          <template slot="append">
            <v-btn
                class=search_btn
                value="search"
                @click="getYpoSearchSuggestions"
            >
              SEARCH
            </v-btn>
          </template>
        </v-text-field>
      </v-flex>
    </v-layout>
    <v-layout v-if="displayTray && !hideSearchTrayFromHeader" column>
      <v-flex sm12 v-if="displaySearchResults" column class="search-results" :style="styleIfSysMsgAppears">
        <v-layout v-if="showNarrowYourResult" wrap>
          <v-flex class="search-results__narrow" xs12 sm12 md12 lg12 xl12>
                        <span>{{
                            `${globalSearchConstants.NARROW_YOUR_SEARCH} "${capitalizeWord(trimmedSearchText())}"`
                          }}</span>
          </v-flex>
        </v-layout>
        <v-layout class="search-results__items" row wrap>
          <v-flex lg3 md3 sm6 xs12 v-if="hasPeoplePropsAndAPIData">
            <people-tile
                :ypo-search-text="trimmedSearchText()"
                :people-tile-search-obj="peopleSearchSuggestions"
                :post-select-callback="globalPostSelectCallback"
                :search-completed="searchCompleted"
            ></people-tile>
          </v-flex>
          <v-flex lg3 md3 sm6 xs12 v-if="hasEventPropsAndAPIData">
            <event-tile
                :ypo-search-text="trimmedSearchText()"
                :event-search-obj="eventSearchSuggestions"
                :post-select-callback="globalPostSelectCallback"
                :show-voucher-one-click="showVoucherOneClick"
                :search-completed="searchCompleted"
            ></event-tile>
          </v-flex>
          <v-flex lg3 md3 sm6 xs12 v-if="hasCommunityPropsAndAPIData">
            <community-tile
                :ypo-search-text="trimmedSearchText()"
                :community-search-obj="communitySearchSuggestions"
                :post-select-callback="globalPostSelectCallback"
                :search-completed="searchCompleted"
            ></community-tile>
          </v-flex>
          <v-flex lg3 md3 sm6 xs12 v-if="hasContentPropsAndAPIData">
            <content-tile
                :ypo-search-text="trimmedSearchText()"
                :content-search-obj="contentSearchSuggestions"
                :post-select-callback="globalPostSelectCallback"
                :search-completed="searchCompleted"
            ></content-tile>
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex sm12>
        <v-layout v-if="hasNoResults" class="search-results" :style="styleIfSysMsgAppears" wrap>
          <v-flex xs9 sm8 md6 lg5 xl4 class="no-search-result">
            <no-result-found :post-select-callback="globalPostSelectCallback"
                             :search-text="trimmedSearchText()"></no-result-found>
          </v-flex>
        </v-layout>
        <v-layout v-if="this.ypoSearchText && showOneCharInterstitial" class="search-results"
                  :style="styleIfSysMsgAppears" wrap>
          <v-flex xs9 sm8 md6 lg5 xl4 class="no-search-result">
            <one-character-interstitial :search-text="trimmedSearchText()"></one-character-interstitial>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import { globalSearchConstants } from './ypoSearch.constants';
import PeopleTile from './PeopleTile';
import EventTile from './EventTile';
import CommunityTile from './CommunityTile';
import ContentTile from './ContentTile';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { storeAction } from '../../store/storeActions.constant';
import { storeGetters } from '../../store/storeGetters.constant';
import { storeMutation } from '../../store/storeMutations.constant';
import NoResultFound from './NoResultFound';
import OneCharacterInterstitial from './OneCharacterInterstitial';
import { gaDataLayerEvent } from "@/plugins/analytics";
import { gtmEventName } from "@/plugins/gtmAnalytics";

export default {
  name: 'GlobalSearchBar',
  components: {
    PeopleTile,
    NoResultFound,
    EventTile,
    CommunityTile,
    ContentTile,
    OneCharacterInterstitial,
  },
  props: {
    postSelectCallback: {
      type: Function,
      default: () => {
      },
    },
    showBrowserMessage: {
      type: Boolean,
    },
    isDeviceIpad: {
      type: Boolean,
    },
    onFocusCallback: {
      type: Boolean,
    },
    hideSearchTrayFromHeader: {
      type: Boolean,
    },
  },
  data: () => ({
    globalSearchConstants,
    ypoSearchText: '',
    searchCompleted: false,
    hideSearchBar: false,
    showOneCharInterstitial: false,
    showNarrowYourResult: false,
    displayTray: false,
    searchError: false,
  }),
  methods: {
    ...mapActions({
      getPeopleSearchSuggestions: `ypoSearch/${storeAction.getPeopleSearchSuggestions}`,
      getEventSearchSuggestions: `ypoSearch/${storeAction.getEventSearchSuggestions}`,
      getCommunitySearchSuggestions: `ypoSearch/${storeAction.getCommunitySearchSuggestions}`,
      getContentSearchSuggestions: `ypoSearch/${storeAction.getContentSearchSuggestions}`,
      initiateTracking: `ypoSearch/${storeAction.initiateTracking}`,
    }),
    ...mapMutations({
      setPeopleSearchSuggestions: `ypoSearch/${storeMutation.setPeopleSearchSuggestions}`,
      setEventSearchSuggestions: `ypoSearch/${storeMutation.setEventSearchSuggestions}`,
      setCommunitySearchSuggestions: `ypoSearch/${storeMutation.setCommunitySearchSuggestions}`,
      setContentSearchSuggestions: `ypoSearch/${storeMutation.setContentSearchSuggestions}`,
      resetSearchSuggestions: `ypoSearch/${storeMutation.resetSearchSuggestions}`,
    }),
    clearSearchText(clearSearchText) {
      this.ypoSearchText = clearSearchText ? '' : this.ypoSearchText;
    },
    bringInTheTray() {
      if (this.onFocusCallback && this.trimmedSearchText()) {
        this.displayTray = true;
        this.hideSearchTrayFromHeader = false;
        this.$emit('clicked-searchInput', this.hideSearchTrayFromHeader);
      }
    },
    globalPostSelectCallback() {
      if (this.postSelectCallback) this.postSelectCallback();
      setTimeout(() => {
        this.displayTray = false;
      }, 400);
    },
    trimmedSearchText() {
      return this.ypoSearchText && this.ypoSearchText.trim();
    },
    hasPeoplePropsAndAPIData() {
      return (
          this.trimmedSearchText() &&
          this.trimmedSearchText().length > 0 &&
          this.peopleSearchSuggestions &&
          this.peopleSearchSuggestions.length > 0
      );
    },
    hasEventPropsAndAPIData() {
      return (
          this.trimmedSearchText() &&
          this.trimmedSearchText().length > 0 &&
          this.eventSearchSuggestions &&
          this.eventSearchSuggestions.length > 0
      );
    },
    hasCommunityPropsAndAPIData() {
      return (
          this.trimmedSearchText() &&
          this.trimmedSearchText().length > 0 &&
          this.communitySearchSuggestions &&
          this.communitySearchSuggestions.length > 0
      );
    },
    hasContentPropsAndAPIData() {
      return (
          this.trimmedSearchText() &&
          this.trimmedSearchText().length > 0 &&
          this.contentSearchSuggestions &&
          this.contentSearchSuggestions.length > 0
      );
    },
    makeYpoSearchAPICalls() {
      const peopleSearchRequest = this.getPeopleSearchSuggestions(this.trimmedSearchText());
      const eventSearchRequest = this.getEventSearchSuggestions(this.trimmedSearchText());
      const communitySearchRequest = this.getCommunitySearchSuggestions(this.trimmedSearchText());
      const contentSearchRequest = this.getContentSearchSuggestions(this.trimmedSearchText());
      gaDataLayerEvent(gtmEventName.C_Global_Search, this.trimmedSearchText());
      Promise.all([peopleSearchRequest,
        eventSearchRequest,
        communitySearchRequest,
        contentSearchRequest])
          .then(() => {
            this.searchCompleted = true;
            const trackingObject = {
              committedSearchTerm: this.trimmedSearchText(),
            };
            if (this.hasNoResults) {
              this.initiateTracking({
                ...trackingObject,
                interstitalType: globalSearchConstants.INTERSTITIAL_NO_RESULTS,
              });
            } else if (this.showNarrowYourResult) {
              this.initiateTracking({
                ...trackingObject,
                interstitalType: globalSearchConstants.INTERSTITIAL_NARROW_SEARCH,
              });
            }
          });
    },
    getYpoSearchSuggestions() {
      this.searchCompleted = false;
      this.showNarrowYourResult = false;
      this.showOneCharInterstitial = false;
      this.resetSearchSuggestions();
      const formattedSearchText = this.trimmedSearchText();
      this.hideSearchTrayFromHeader = true;
      this.displayTray = true;
      if (this.trimmedSearchText().length > 1) {
        this.hideSearchTrayFromHeader = false;
        this.showNarrowYourResult = false;
        this.makeYpoSearchAPICalls();
        this.searchError = false;
      } else if (formattedSearchText.length === 1) {
        this.searchError = true;
      }
    },

    handleKeyup(e) {
      const formattedSearchText = this.trimmedSearchText();
      this.displayTray = false;
      if (formattedSearchText.length) {
        if (e.keyCode === 13) {
          if (formattedSearchText.length === 1) {
            this.searchError = true;
          } else if (formattedSearchText.length >= 2) {
            this.searchCompleted = false;
            this.showNarrowYourResult = false;
            this.showOneCharInterstitial = false;
            this.resetSearchSuggestions();
            this.hideSearchTrayFromHeader = false;
            this.displayTray = true;
            this.makeYpoSearchAPICalls();
          }
        } else {
          this.searchError = false;
        }
      } else {
        this.searchError = false;
      }
    },
    capitalizeWord(word) {
      return word.toLocaleUpperCase();
    },
    toggleTray() {
      this.displayTray = false;
      this.searchError = false;
    },
  },
  computed: {
    showVoucherOneClick() {
      return this.trimmedSearchText().toLowerCase().includes(globalSearchConstants.VOUCHER || globalSearchConstants.VOUCHERS);
    },
    styleIfSysMsgAppears() {
      if (this.showBrowserMessage) {
        if (this.isDeviceIpad) {
          return globalSearchConstants.TOP_MARGIN_CSS_SMALL_IPAD;
        }
        return globalSearchConstants.TOP_MARGIN_CSS_REG_IPAD;
      }
    },
    displaySearchResults() {
      return this.trimmedSearchText() && this.trimmedSearchText().length > 1;
    },
    hasNoResults() {
      return (
          this.trimmedSearchText() &&
          this.trimmedSearchText().length >= 2 &&
          this.searchCompleted &&
          this.peopleSearchSuggestions &&
          this.peopleSearchSuggestions.searchResults &&
          this.peopleSearchSuggestions.searchResults.length <= 0 &&
          this.eventSearchSuggestions &&
          this.eventSearchSuggestions.searchResults &&
          this.eventSearchSuggestions.searchResults.length <= 0 &&
          this.communitySearchSuggestions &&
          this.communitySearchSuggestions.searchResults &&
          this.communitySearchSuggestions.searchResults.length <= 0 &&
          this.contentSearchSuggestions &&
          this.contentSearchSuggestions.searchResults &&
          this.contentSearchSuggestions.searchResults.length <= 0
      );
    },
    ...mapGetters({
      peopleSearchSuggestions: `ypoSearch/${storeGetters.peopleSearchSuggestions}`,
      eventSearchSuggestions: `ypoSearch/${storeGetters.eventSearchSuggestions}`,
      communitySearchSuggestions: `ypoSearch/${storeGetters.communitySearchSuggestions}`,
      contentSearchSuggestions: `ypoSearch/${storeGetters.contentSearchSuggestions}`,
    }),
    ...mapState('actionState', ['actions']),
  },
};
</script>

<style lang="scss" scoped>
@import '../../common/styles/_commonClasses.scss';
@import '../../common/styles/_responsive.scss';

.search-bar {
    flex: initial;
    margin-left: auto;
    height: 27px;
    width: 380px;
    color: $black;
    font-family: 'Noto Sans', serif;
    font-size: 14px;

    @include breakpoint(xs) {
        width: 100%;
    }

  @include breakpoint(sm) {
    width: 100%;
  }

  &--error {
    border: 3px solid $ypo-red;
    height: 36px;
    border-radius: 10px;
  }

  .search_btn {
    color: $white !important;
    border-radius: 5px;
    background: $ypo-gold !important;
    padding: 6px 9px;
    height: 30.5px;
    font-size: 14px;
    font-weight: 600;
    box-shadow: none !important;
    top: -7px;
    right: 0px;
  }
}

.search-results {
  box-shadow: 0 4px 1px 0 rgba(0, 0, 0, 0.2);
  background-color: $white;
  width: 100%;
  height: 100%;
  top: 64px;
  position: fixed;
  right: 0;
  left: 0;
  z-index: 1000;
  overscroll-behavior: contain;

  &__items {
    @include breakpoint(sm) {
      margin-bottom: 140px;
    }
    @include breakpoint(xs) {
      margin-bottom: 350px;
    }
  }

  @include breakpoint(sm) {
    overflow-y: scroll;
  }

  @include breakpoint(xs) {
    overflow-y: scroll;
    top: 50px;
  }

  @media (min-width: $screen-xs) and (max-width: $screen-sm) {
    top: 125px;
  }

  &__narrow {
    height: 20px;
    width: 383px;
    color: $black;
    font-family: Raleway, serif;
    font-size: 22px;
    font-weight: 500;
    line-height: 20px;
    text-align: center;
    margin: 40px;
  }
}

.no-search-result {
  margin: auto;
  height: 100%;
  top: 64px;
  right: 0;
  left: 0;
  z-index: 1000;
  overflow: scroll;

  @include breakpoint(xs) {
    top: 55px;
  }

  @media (min-width: $screen-xs) and (max-width: $screen-sm) {
    top: 125px;
  }
}
</style>

<style lang="scss">
@import '../../common/styles/_commonClasses.scss';

.search-bar {
  .v-input--is-dirty {
    .v-input__append-inner:not(.v-input__append-inner ~ .v-input__append-inner) {
      background-color: $ypo-cta;
      padding: 0;
      border-radius: 50%;
      margin-top: 5px !important;
      margin-right: -5px !important;

      .v-input__icon {
        width: 18px;
        height: 18px;
        min-width: 18px;
        min-height: 18px;

        .v-icon {
          color: $white !important;
          font-size: 12px;
          font-weight: 600;
        }
      }
    }
  }
}
</style>
